import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { authApi } from '../api/auth';
import { setAuthToken } from '../api/client';
import { progressApi } from '../api/progress';
import type { AuthResponse, CodeSentResult } from '../api/auth.types';

const TOKEN_KEY = 'torii_auth_token';

/**
 * `checking` — đang khôi phục phiên từ localStorage, chưa biết có đăng nhập hay không.
 * Không có chế độ khách: mọi màn hình đều lấy dữ liệu từ API nên bắt buộc phải đăng nhập,
 * giống apps/mobile.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  /** false = tài khoản chỉ đăng nhập Google — xoá tài khoản không hỏi mật khẩu. */
  hasPassword: boolean;
}

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  /** Đăng nhập bằng ID token Google. `isNewUser` = tài khoản vừa được tạo. */
  loginWithGoogle: (idToken: string) => Promise<{ isNewUser: boolean }>;
  /** Bước 1 đăng ký: chỉ gửi mã về email, CHƯA đăng nhập. Gọi lại = gửi lại mã. */
  register: (email: string, password: string, displayName?: string) => Promise<CodeSentResult>;
  /** Bước 2 đăng ký: đúng mã thì tạo tài khoản và đăng nhập. */
  verifyRegistration: (email: string, code: string) => Promise<void>;
  /** Quên mật khẩu bước 1: gửi mã đặt lại về email. */
  forgotPassword: (email: string) => Promise<CodeSentResult>;
  /** Quên mật khẩu bước 2: đúng mã thì đổi mật khẩu và đăng nhập. */
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  logout: () => void;
  updateDisplayName: (displayName: string) => Promise<void>;
  /** `password` bỏ trống với tài khoản chỉ đăng nhập Google. */
  deleteAccount: (password?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Token nằm ở localStorage để giữ phiên qua lần mở trình duyệt sau. Trình duyệt
// chặn storage ở chế độ riêng tư nên mọi truy cập đều bọc try/catch.
const store = {
  getToken: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  setToken: (token: string): void => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* phiên chỉ sống trong bộ nhớ */
    }
  },
  clearToken: (): void => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* không có gì để dọn */
    }
  },
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    const token = store.getToken();
    if (!token) {
      setStatus('unauthenticated');
      return;
    }

    setAuthToken(token);
    void (async () => {
      try {
        const me = await progressApi.getMe();
        if (cancelled) return;
        setUser({ id: me.id, email: me.email ?? '', displayName: me.displayName, hasPassword: me.hasPassword });
        setStatus('authenticated');
      } catch {
        // Token hết hạn/không hợp lệ, hoặc API không chạy — dọn sạch, coi như chưa đăng nhập.
        store.clearToken();
        setAuthToken(null);
        if (!cancelled) setStatus('unauthenticated');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /** Lưu token + user sau mọi cách đăng nhập (mật khẩu, xác minh đăng ký, đặt lại mật khẩu). */
  const signIn = (res: AuthResponse) => {
    store.setToken(res.accessToken);
    setAuthToken(res.accessToken);
    setUser(res.user);
    setStatus('authenticated');
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login: async (email, password) => signIn(await authApi.login({ email, password })),
      loginWithGoogle: async (idToken) => {
        const res = await authApi.google(idToken);
        await signIn(res);
        return { isNewUser: res.isNewUser };
      },
      register: (email, password, displayName) => authApi.register({ email, password, displayName }),
      verifyRegistration: async (email, code) => signIn(await authApi.verifyRegistration({ email, code })),
      forgotPassword: (email) => authApi.forgotPassword({ email }),
      resetPassword: async (email, code, newPassword) => signIn(await authApi.resetPassword({ email, code, newPassword })),
      logout: () => {
        store.clearToken();
        setAuthToken(null);
        setUser(null);
        setStatus('unauthenticated');
      },
      updateDisplayName: async (displayName) => {
        await progressApi.updateProfile(displayName);
        setUser((u) => (u ? { ...u, displayName } : u));
      },
      deleteAccount: async (password) => {
        await progressApi.deleteAccount(password);
        store.clearToken();
        setAuthToken(null);
        setUser(null);
        setStatus('unauthenticated');
      },
    }),
    [status, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth phải dùng bên trong AuthProvider');
  return ctx;
}
